import { motion, AnimatePresence } from "framer-motion";
import { X, Network, Copy, Share2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../../App";

/**
 * Right-side drawer with the merchant's MLM QR code + referral link + network stats.
 * Hidden from the home (the cliente premium spec moved the QR off the main view).
 */
export default function NetworkDrawer({ open, onClose, user }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await fetch(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setStats(await r.json());
      } catch {}
    })();
  }, [open]);

  const code = user?.referral_code || "";
  const link = `${window.location.origin}/register/${code}`;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60"
            onClick={onClose}
            data-testid="network-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 360, damping: 32 }}
            className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-[#13161A] border-l border-white/5 flex flex-col"
            data-testid="network-drawer"
          >
            <header className="h-16 px-5 flex items-center justify-between border-b border-white/5">
              <div className="flex items-center gap-2">
                <Network className="w-5 h-5 text-[#00D68F]" />
                <h2 className="font-extrabold">Minha rede MLM</h2>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-white/5 flex items-center justify-center" data-testid="network-drawer-close">
                <X className="w-5 h-5 text-white/60" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* QR Code */}
              <div className="bg-white rounded-2xl p-5 flex justify-center" data-testid="network-qr-wrap">
                {code && (
                  <QRCode value={link} size={200} bgColor="#FFFFFF" fgColor="#0B0D0F" />
                )}
              </div>

              {/* Code */}
              <div className="bg-[#0B0D0F] border border-white/5 rounded-2xl p-4 text-center">
                <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Seu código</p>
                <p className="text-3xl font-extrabold tracking-[0.3em] mt-1 text-[#00D68F]" data-testid="network-drawer-code">{code || "—"}</p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiado!"); }}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 rounded-full py-2 text-xs font-bold"
                    data-testid="network-drawer-copy"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar link
                  </button>
                  <button
                    onClick={() => {
                      if (navigator.share) navigator.share({ title: "GoFood", text: `Use meu código ${code}`, url: link }).catch(() => {});
                      else { navigator.clipboard.writeText(link); toast.success("Link copiado!"); }
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-[#00A86B] hover:bg-[#00D68F] rounded-full py-2 text-xs font-bold"
                    data-testid="network-drawer-share"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Compartilhar
                  </button>
                </div>
              </div>

              {/* Stats */}
              {stats && (
                <div className="grid grid-cols-2 gap-2" data-testid="network-drawer-stats">
                  <Stat label="Indicados diretos" value={stats.direct_referrals ?? 0} accent="#00D68F" />
                  <Stat label="Rede total" value={stats.total_network ?? 0} accent="#FF6B00" />
                  <Stat label="Total ganho" value={`R$ ${Number(stats.total_earned || 0).toLocaleString("pt-BR", {minimumFractionDigits:2})}`} accent="#00D68F" />
                  <Stat label="Pedidos" value={stats.total_orders ?? 0} />
                </div>
              )}

              <p className="text-[11px] text-white/40 text-center">
                Indique outros lojistas, entregadores e clientes — ganhe comissões em até 5 níveis.
              </p>

              {/* CTA pra nova Rede MLM premium */}
              <Link
                to="/loja/rede"
                onClick={onClose}
                data-testid="network-drawer-goto-full"
                className="flex items-center justify-between gap-2 bg-gradient-to-r from-[#FF6B00] to-[#FF8E00] hover:opacity-90 rounded-2xl p-4 transition-opacity"
              >
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-white/90">Novo</p>
                  <p className="text-sm font-extrabold text-white">Ver minha rede completa</p>
                  <p className="text-[11px] text-white/80">Lista · Árvore · Organograma</p>
                </div>
                <ArrowRight className="w-5 h-5 text-white" />
              </Link>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#0B0D0F] border border-white/5 rounded-2xl p-3 text-center">
      <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">{label}</p>
      <p className="text-lg font-extrabold tabular-nums" style={{ color: accent || "white" }}>{value}</p>
    </div>
  );
}
